import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAusfertigungFestschreiben, useVertragFristen, datum, useNachtrag } from "../../hooks/useVertraege";
import type { VertragDetail } from "../../hooks/useVertraege";
import { fehlertext } from "../../lib/fehler";
import { Zeile } from "./teile";

/**
 * Die Ausfertigung — das Dokument, das am Kunden hängt.
 *
 * Vor der Zustimmung gibt es sie noch nicht: dann ist das PDF eine Vorschau aus
 * dem laufenden Stand. Ab der Zustimmung liegt sie unveränderlich in der
 * Datenbank, und jeder Abruf — hier wie unter dem Kundenlink — liefert genau
 * diese Bytes. Die Prüfsumme steht dabei, damit im Zweifel belegbar ist, dass
 * es dasselbe Dokument ist.
 *
 * Für den Papierweg gibt es den Knopf: wer unterschrieben zurückschickt, geht
 * nie über die Zustimmungsseite und hätte sonst nie eine feste Fassung.
 */
export function Ausfertigungskarte({ v }: { v: VertragDetail }) {
  const festschreiben = useAusfertigungFestschreiben(v.id);
  const [meldung, setMeldung] = useState("");
  const a = v.ausfertigung;

  if (v.status === "entwurf") return null;

  // Online zugestimmt, aber nichts festgeschrieben: dann ist bei der
  // Zustimmung etwas schiefgegangen — und meist auch die Bestätigungsmail
  // nicht rausgegangen (PV-2026-006, 06.10.2026). Das muss laut dastehen.
  const fehlt = !a && !!v.zustimmung?.zugestimmt_am;

  if (!a) {
    return (
      <div className="pf-card" style={{ marginBottom: 16, ...(fehlt ? { borderLeft: "3px solid var(--pf-red)" } : {}) }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          {fehlt ? "Ausfertigung fehlt" : "Ausfertigung"}
        </div>
        <div style={{ fontSize: 13, color: "var(--pf-ink-3)", lineHeight: 1.5 }}>
          {fehlt
            ? "Der Kunde hat online zugestimmt, aber das Dokument wurde nicht festgeschrieben. Vermutlich hat er auch keine Bestätigungsmail bekommen. Der Knopf holt beides nach."
            : "Noch nicht festgeschrieben. Bis dahin zeigt „So sieht es der Kunde“ den laufenden Stand, und der ändert sich mit jeder Bearbeitung. Mit der Zustimmung des Kunden wird das Dokument automatisch festgeschrieben."}
        </div>
        <button className={fehlt ? "pf-btn is-small" : "pf-btn is-ghost is-small"} style={{ marginTop: 10 }}
          disabled={festschreiben.isPending}
          onClick={() => festschreiben.mutateAsync()
            .then(() => setMeldung(fehlt ? "Festgeschrieben, Bestätigung verschickt." : "Ausfertigung festgeschrieben."))
            .catch(e => setMeldung(fehlertext(e) || "Fehlgeschlagen."))}>
          {festschreiben.isPending
            ? "Schreibe fest…"
            : fehlt ? "Festschreiben und Bestätigung senden" : "Unterschrieben erhalten — jetzt festschreiben"}
        </button>
        {meldung && <div style={{ fontSize: 12.5, marginTop: 8, color: "var(--pf-ink-3)" }}>{meldung}</div>}
      </div>
    );
  }

  return (
    <div className="pf-card" style={{ marginBottom: 16, borderLeft: "3px solid var(--pf-accent)" }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Ausfertigung festgeschrieben</div>
      <Zeile l="Festgeschrieben am" r={new Date(a.erstellt_am).toLocaleString("de-DE")} />
      <Zeile l="Anlass" r={a.anlass === "zustimmung" ? "Online-Zustimmung" : "Unterschrift auf Papier"} />
      <Zeile l="Prüfsumme" r={a.sha_kurz} />
      <Zeile l="Größe" r={`${Math.round(a.groesse / 1024)} KB · Vorlage V${a.vorlage_version}`} />
      {a.nextcloud_pfad && <Zeile l="Nextcloud" r={a.nextcloud_pfad} />}
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        {/* Jedes Blatt einzeln — die Urkunde allein hat kein Leistungsverzeichnis,
            das steht in Anlage 1 (Benjamin, 09.10.2026: „das Leistungsverzeichnis fehlt"). */}
        {(a.teile && a.teile.length > 0 ? a.teile : [{ teil: "urkunde", titel: "Jahrespflegevertrag", anzeige: "Jahrespflegevertrag", groesse: 0 }]).map(t => (
          <a key={t.teil} className="pf-btn is-ghost is-small" href={`/api/pflege/vertraege/${v.id}/pdf?teil=${t.teil}`}
            target="_blank" rel="noreferrer"
            style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
            {t.anzeige || t.titel}
          </a>
        ))}
      </div>
      {meldung && <div style={{ fontSize: 12.5, marginTop: 8, color: "var(--pf-ink-3)" }}>{meldung}</div>}
      <div style={{ fontSize: 12, color: "var(--pf-ink-3)", marginTop: 10, lineHeight: 1.5 }}>
        Unter dem Kundenlink (Vertrag und Dokumente) liegt dauerhaft genau diese
        Datei — spätere Änderungen an der Vorlage fassen sie nicht mehr an.
      </div>
    </div>
  );
}

/**
 * Laufzeit und Fristen — gerechnet, nicht abgeschrieben.
 *
 * Dieselbe Quelle wie die Fristenübersicht (`vertrag_fristen.py`): Erstlaufzeit,
 * letzter Kündigungstermin, was eine heute eingehende Kündigung bewirken würde.
 * Die letzte Zeile ist die wichtigste — sie beantwortet die Frage, die im
 * Telefonat gestellt wird („wann komme ich raus?"), ohne dass jemand rechnet.
 */
export function Fristenkarte({ v }: { v: VertragDetail }) {
  const { data: f } = useVertragFristen(v.id);
  if (v.status === "entwurf" || !f) return null;
  if (!f.beginn) {
    return (
      <div className="pf-card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Fristen</div>
        <div style={{ fontSize: 13, color: "var(--pf-ink-3)" }}>{f.hinweis}</div>
      </div>
    );
  }
  const gekuendigt = !!f.gekuendigt_zum;
  return (
    <div className="pf-card" style={{ marginBottom: 16 }}>
      <Zeile l="Beginn" r={datum(f.beginn)} />
      <Zeile l="Erstlaufzeit endet" r={datum(f.erstlaufzeit_ende)} />
      <Zeile l="Verlängerung" r={f.verlaengerung === "unbestimmt"
        ? "auf unbestimmte Zeit" : `um jeweils ${f.verlaengerung}`} />
      {gekuendigt ? (
        <Zeile l="Gekündigt zum" r={datum(f.gekuendigt_zum)} strong />
      ) : (
        <>
          <Zeile
            l={f.laeuft_unbefristet ? "Kündbar" : "Letzter Kündigungstermin"}
            r={f.laeuft_unbefristet
              ? "jederzeit"
              : `${datum(f.naechster_kuendigungstermin)}${
                  f.tage_bis_kuendigungstermin !== null && f.tage_bis_kuendigungstermin !== undefined
                    ? ` · noch ${f.tage_bis_kuendigungstermin} Tage` : ""}`} />
          <Zeile l="Kündigung heute wirkt zum" r={datum(f.kuendigung_heute_wirksam_zum)} strong />
        </>
      )}
      {f.in_widerrufsfrist && (
        <div style={{ fontSize: 12.5, color: "var(--pf-ink-2)", marginTop: 8, lineHeight: 1.5 }}>
          Der Kunde kann diesen Vertrag noch bis {datum(f.widerruf_ablauf)} widerrufen.
        </div>
      )}
      <div style={{ fontSize: 12, color: "var(--pf-ink-3)", marginTop: 8 }}>
        Preisanpassung frühestens zum {datum(f.preisanpassung_fruehestens)}.
      </div>
    </div>
  );
}

/**
 * Nachtrag — eine neue Fassung des Vertrages.
 *
 * Ein Nachtrag ist kein Bearbeiten: er entsteht als eigener Vertrag mit
 * `vorgaenger_id`, eigener Version und eigener Nummer (PV-…-N2). Der Vorgänger
 * bleibt unverändert stehen — was der Kunde einmal unterschrieben hat, wird
 * nicht überschrieben.
 *
 * Ist eine Preisanpassung angekündigt (§ 8), ist der Prozentsatz der Anlass:
 * die Karte schlägt den Termin vor und hebt beim Anlegen alle Einzelpreise an.
 */
export function Nachtragskarte({ v }: { v: VertragDetail }) {
  const navigate = useNavigate();
  const nachtrag = useNachtrag(v.id);
  const [prozent, setProzent] = useState(0);
  const [fehler, setFehler] = useState("");
  const angekuendigt = v.preisanpassung_zum;
  const faellig = !!angekuendigt && new Date(angekuendigt) <= new Date();

  if (v.status !== "aktiv") return null;

  return (
    <div className="pf-card" style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", lineHeight: 1.5 }}>
        Eine neue Fassung mit eigener Nummer. Der laufende Vertrag bleibt unverändert
        stehen, bis der Kunde dem Nachtrag zustimmt.
      </div>

      {angekuendigt && (
        <div style={{ fontSize: 13, marginTop: 10, color: "var(--pf-ink-2)", lineHeight: 1.5 }}>
          Preisanpassung angekündigt zum <strong>{datum(angekuendigt)}</strong>
          {faellig ? " — jetzt fällig." : " — noch nicht fällig."}
        </div>
      )}

      <div className="pf-grid-2" style={{ marginTop: 10 }}>
        <label className="pf-field"><span>Preise anheben um (%)</span>
          <input className="pf-input" type="number" step="0.5" min={0} max={25} value={prozent}
            onChange={e => setProzent(Number(e.target.value) || 0)} /></label>
      </div>
      {prozent > 0 && !angekuendigt && (
        <div style={{ fontSize: 12.5, color: "var(--pf-ink-2)", marginTop: 8, lineHeight: 1.5 }}>
          Für diesen Vertrag ist keine Preisanpassung angekündigt. § 8 verlangt acht Wochen
          Vorlauf in Textform — erst ankündigen (unter „Fristen"), dann den Nachtrag anlegen.
        </div>
      )}

      <button className="pf-btn is-ghost" style={{ marginTop: 10 }} disabled={nachtrag.isPending}
        onClick={async () => {
          setFehler("");
          try {
            const neu = await nachtrag.mutateAsync({
              prozent, beginn: faellig ? angekuendigt : null,
            });
            navigate(`/vertraege/neu?entwurf=${neu.id}`);
          } catch (e) { setFehler(fehlertext(e) || "Fehlgeschlagen."); }
        }}>
        {nachtrag.isPending ? "Lege an…"
          : prozent > 0 ? `Nachtrag mit +${prozent} % anlegen` : "Nachtrag anlegen"}
      </button>
      {fehler && <div style={{ fontSize: 12.5, marginTop: 8, color: "var(--pf-red)" }}>{fehler}</div>}
    </div>
  );
}
